/**
 * The only data path to the backend: a typed client generated from the FastAPI OpenAPI schema.
 * - cookie sessions: every call names this tab's identity slot (X-VP-Slot) and unsafe methods echo that slot's
 *   CSRF cookie (vp_csrf_<slot>, or vp_csrf without a slot) in X-CSRF
 * - every write carries an Idempotency-Key (callers may pass their own to make retries safe)
 * - errors are thrown as ApiError with the server's {error, status, details}
 */
import createClient, { type Middleware } from "openapi-fetch";
import { apiFileUrl, csrfCookieName, getSlot } from "../lib/identity";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];
export type { paths };

export class ApiError extends Error {
  status: number;
  details: string[];
  body: Record<string, unknown>;
  constructor(status: number, body: Record<string, unknown>) {
    super(typeof body.error === "string" ? body.error : `HTTP ${status}`);
    this.status = status;
    this.details = Array.isArray(body.details) ? (body.details as string[]) : [];
    this.body = body;
  }
}

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const [k, ...v] = part.trim().split("=");
    if (k === name) return decodeURIComponent(v.join("="));
  }
  return null;
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const authMiddleware: Middleware = {
  onRequest({ request }) {
    const slot = getSlot();
    if (slot && !request.headers.has("X-VP-Slot")) request.headers.set("X-VP-Slot", slot);
    if (UNSAFE.has(request.method)) {
      const csrf = readCookie(csrfCookieName(slot));
      if (csrf && !request.headers.has("X-CSRF")) request.headers.set("X-CSRF", csrf);
      if (!request.headers.has("Idempotency-Key")) request.headers.set("Idempotency-Key", newIdempotencyKey());
    }
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: "", credentials: "same-origin" });
api.use(authMiddleware);

/** Unwrap an openapi-fetch result: data, or throw ApiError. */
export async function unwrap<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await p;
  if (!response.ok) {
    const body = (error && typeof error === "object" ? error : { error: String(error ?? response.statusText) }) as
      Record<string, unknown>;
    throw new ApiError(response.status, body);
  }
  return data as T;
}

/** Header for optimistic concurrency: pass the version you read. */
export function ifMatch(version: number | null | undefined): Record<string, string> {
  return version == null ? {} : { "If-Match": String(version) };
}

/** Media URLs carry this tab's identity slot (`?slot=`): <img>/<video>/<a download> cannot send headers. */
export const mediaUrl = (id: number) => apiFileUrl(`/api/media/${id}`);
export const mediaDownloadUrl = (id: number) => apiFileUrl(`/api/media/${id}`, { download: 1 });
